import React from "react";
import SEO from "../components/SEO";

const RefundPolicy = () => {
  return (
    <div className="max-w-5xl mx-auto p-6 text-gray-800 leading-relaxed">
      <SEO
        title="Cancellation & Refund Policy | InvestorLanes"
        description="Read InvestorLanes' Cancellation and Refund Policy for subscription plans, billing, cancellations, reversals, and compliance with the South African CPA."
        canonicalUrl="https://www.investorlanes.com/refund-policy"
      />

      <h1 className="text-4xl font-bold mb-4">Cancellation & Refund Policy</h1>
      <p className="text-gray-500 text-sm mb-6"><strong>Last Updated:</strong> August 2026</p>

      <p className="mb-6">
        At <strong>InvestorLanes</strong>, operated by <strong>InvestorLanes</strong> 
        (Midrand, Johannesburg, South Africa), we are committed to professional transparency. This Cancellation & Refund Policy 
        governs all premium digital subscriptions and paywall transactions processed on our Platform. 
      </p>

      <p className="mb-6">
        Our billing mechanisms are designed to comply fully with the <strong>South African Consumer Protection Act, No. 68 of 2008 (CPA)</strong> 
        and Electronic Communications and Transactions Act (ECTA) regulations, alongside regional pan-African financial merchant guidelines.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">1. Premium Subscription Access</h2>
      <p className="mb-6">
        InvestorLanes offers premium tier subscription access to unlock advanced networking capabilities, proprietary investor directories, 
        and direct algorithmic match messaging. Because access to our data pools and connection parameters is delivered immediately 
        upon successful payment authentication, digital subscription service points are considered fully utilized upon account provisioning.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">2. Currency and Automatic Billing Protocol</h2>
      <p className="mb-4">
        All financial token routing is executed securely through our integrated primary payment gateway.
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li><strong>Currency Standard:</strong> Payments are billed in South African Rand (ZAR) or equivalent regional currency options matching your card origin, as presented at checkout.</li>
        <li><strong>Recurring Cycles:</strong> Premium accounts are configured to auto-renew on a recurring monthly or annual timeline. Your payment profile stored securely within our Payment gateway encrypted vault will be automatically debited at the start of each subsequent billing window.</li>
      </ul>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">3. Dash-Controlled Cancellation Pipeline</h2>
      <p className="mb-6">
        You retain the absolute right to cancel your premium subscription model at any point. To stop recurring charges, you must execute a 
        cancellation directly inside your active <strong>Platform Account Settings</strong> panel, or submit a formal ticket to our service desk. 
        Following active cancellation, your advanced features will persist uninterrupted until the expiration date of your current prepaid period. 
        We do not issue pro-rated credits or partial balances for unused segments within a single billing term.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">4. Statutory Cooling-Off Rights & Valid Refund Triggers</h2>
      <p className="mb-4">
        In strict accordance with Section 16 of the South African Consumer Protection Act, if you concluded your subscription via electronic direct 
        marketing, you possess a statutory <strong>5-day cooling-off window</strong> to rescind the transaction without penalty. Beyond statutory 
        provisions, InvestorLanes evaluates standard refund submissions on a strict case-by-case basis. Valid grounds for standard reversals include:
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>Verified double or duplicate payment executions caused by browser timeout cycles.</li>
        <li>Demonstrable technical system errors on the InvestorLanes architecture that entirely block access to paid features.</li>
        <li>A fraudulent, unverified charge flagged before internal validation pipelines.</li>
      </ul>
      <p className="mb-6">
        All formal refund application claims must be submitted in writing within <strong>14 calendar days</strong> of the original charge timestamp.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">5. Absolute Non-Refundable Scenarios</h2>
      <p className="mb-4"> InvestorLanes will explicitly deny compensation or fee reversals under the following conditions:</p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>Failure to secure active capitalization, investments, or matching partnership closures (we sell platform utility, not deal outcomes).</li>
        <li>Forgetting to terminate your active dashboard tier prior to an automated recurring renewal date.</li>
        <li>Voluntary inactivity or change of corporate strategy by your startup or enterprise entity.</li>
        <li>Account blockades or asset terminations arising from severe breaches of our explicit Terms of Service.</li>
      </ul>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">6. Processing Timelines & Chargeback Prevention</h2>
      <p className="mb-6">
        Approved reimbursement requests are systematically routed and reversed directly back into the originating credit/debit card account via the 
        Payment agency API infrastructure. Financial institutions generally take between <strong>7 to 14 business days</strong> to finalize settlement data 
        and reflect the cleared capital balance on your statement.
      </p>
      <p className="mb-6">
        <strong>Pre-Dispute Notice:</strong> If you suspect a billing error or unauthorized item, you agree to contact InvestorLanes support before 
        initiating a formal chargeback request with your banking institution. Misusing chargeback protocols to avoid premium tier service fees 
        (friendly fraud) violates merchant safety terms and will trigger immediate, permanent banishment from the Platform.
      </p>

      <hr className="my-8 border-gray-200" />

      <h2 className="text-2xl font-semibold mb-3">7. Support and Claims Management</h2>
      <p className="mb-4">For subscription cancellations, dispute management, or premium billing clarifications, utilize our dedicated channel:</p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 inline-block min-w-[320px]">
        <p className="mb-1"><strong>Operating Entity:</strong> InvestorLanes </p>
        <p className="mb-1"><strong>Support Interface:</strong> support@investorlanes.com</p>
        <p><strong>Corporate Base:</strong> Midrand, Johannesburg, South Africa</p>
      </div>
    </div>
  );
};

export default RefundPolicy;
